import uniqBy from 'lodash/uniqBy';

import { Issue, defaultFilters } from '@qality/common-model';
import { QAlityFilterestTestCasesParams } from '@qality/common-model/src/JQLSearch';
import { JQLSearchUtils } from '@qality/common-utils/src/JQLSearchUtils';

import JQLSearchService from './JQLSearchService';

type RepositoryIssueSearchResult = {
  issues: Array<Issue>;
  total: number;
  nextPageToken?: string | null;
};

type FinalRepositoryIssueSearchResult = {
  issues: Array<Issue>;
  total: number;
  nextPageTokenForSearchByKeys?: string | null;
  nextPageTokenForSearchByText?: string | null;
};

const getQAlityFilteredTestCasesByText = async (
  nextPageToken: string = null,
  maxResults = JQLSearchUtils.MAX_SEARCH_RESULTS,
  filters = defaultFilters
): Promise<RepositoryIssueSearchResult> => {
  const jqlWithTextSearch = JQLSearchUtils.buildJqlQuery(filters, 'text');

  const response = await JQLSearchService.getIssuePageByJQL({
    jql: jqlWithTextSearch,
    fields: JQLSearchUtils.DEFAULT_FIELDS,
    maxResults,
    nextPageToken,
  });

  const total = await JQLSearchService.countIssuesByJql(jqlWithTextSearch);
  return {
    issues: response.issues,
    nextPageToken: response.nextPageToken,
    total: total,
  };
};

const getQAlityFilteredTestCasesByKeys = async (
  keys: Array<string>,
  nextPageToken: string = null,
  maxResults = JQLSearchUtils.MAX_SEARCH_RESULTS,
  filters = defaultFilters
): Promise<RepositoryIssueSearchResult> => {
  const jqlWithKeySearch = JQLSearchUtils.buildJqlQuery(filters, 'keys', keys);

  const response = await JQLSearchService.getIssuePageByJQL({
    jql: jqlWithKeySearch,
    fields: JQLSearchUtils.DEFAULT_FIELDS,
    maxResults,
    nextPageToken,
    retry: false,
  });

  const total = await JQLSearchService.countIssuesByJql(jqlWithKeySearch);

  return {
    issues: response.issues,
    nextPageToken: response.nextPageToken,
    total: total,
  };
};

const getQAlityFilteredTestCasesCloud = async ({
  nextPageTokenForSearchByText = null,
  nextPageTokenForSearchByKeys = null,
  maxResults = JQLSearchUtils.MAX_SEARCH_RESULTS,
  filters = defaultFilters,
}: QAlityFilterestTestCasesParams): Promise<FinalRepositoryIssueSearchResult> => {
  const issuesWithTextResponse = await getQAlityFilteredTestCasesByText(
    nextPageTokenForSearchByText,
    maxResults,
    filters
  );

  const issuesWithTextCount = issuesWithTextResponse.total;

  const keys = JQLSearchUtils.getValidIssueKeysFromText(filters.text);
  if (keys.length != 0) {
    try {
      const issuesByKeysResponse = await getQAlityFilteredTestCasesByKeys(
        keys,
        nextPageTokenForSearchByKeys,
        maxResults,
        filters
      );

      return {
        issues: uniqBy(
          [...issuesWithTextResponse.issues, ...issuesByKeysResponse.issues],
          (issue) => issue.key
        ),
        total: issuesWithTextCount + issuesByKeysResponse.total,
        nextPageTokenForSearchByText: issuesWithTextResponse.nextPageToken,
        nextPageTokenForSearchByKeys: issuesByKeysResponse.nextPageToken,
      };
    } catch (error) {
      return {
        issues: issuesWithTextResponse.issues,
        total: issuesWithTextCount,
        nextPageTokenForSearchByText: issuesWithTextResponse.nextPageToken,
        nextPageTokenForSearchByKeys: null,
      };
    }
  }
  return {
    issues: issuesWithTextResponse.issues,
    total: issuesWithTextCount,
    nextPageTokenForSearchByText: issuesWithTextResponse.nextPageToken,
    nextPageTokenForSearchByKeys: null,
  };
};

export default {
  getQAlityFilteredTestCasesCloud,
};
